import { type HttpContext } from '@angular/common/http';
import { RowStatusMarkComponent } from '@modules/team-list-state/row-status-mark.component';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  viewChild,
  afterNextRender,
  Injector,
} from '@angular/core';
import {
  BaseStitchListComponent,
  type StitchListItem,
} from '@modules/common/components/list/base-stitch-list.component';
import { ListItemSeparatorComponent } from '@modules/common/components/list/list-items-separator/list-items-separator.component';
import { ListLoadErrorComponent } from '@modules/common/components/list/list-load-error/list-load-error.component';
import { NoContentStubComponent } from '@frontendlib/components/components/no-content-stub/no-content-stub.component';
import { LoadingIndicatorComponent } from '@frontendlib/components/components/loading-indicator/loading-indicator.component';
import { ListSelectionBarComponent } from '@modules/common/components/list/list-selection-bar/list-selection-bar.component';
import { Pagination2Component } from '@modules/common/components/pagination/pagination2.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { SelectableItemDirective } from '@modules/drag-n-drop/directives/selectable.directive';
import { SelectableListDirective } from '@modules/drag-n-drop/directives/selectable-list.directive';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { FileComponent } from '@modules/elements/components/file/file.component';
import { ElementsModule } from '@modules/elements/elements.module';
import { FilesService } from '@modules/files/services/files.service';
import { FilesUploadSelectionService } from '@modules/files/services/files-upload-selection.service';
import { FilesTenantScopeService } from '@modules/files/services/files-tenant-scope.service';
import { FilesAppStateService } from '@modules/files/services/state.service';
import { File } from '@modules/files/types/file';
import { hasFileLink } from '@modules/files/types/file-link';
import { FilesFilters } from '@modules/files/types/files-filters';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { ClickDirective } from '@modules/utils/directives/click/click.directive';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-files-list',
  templateUrl: './files-list.component.html',
  styleUrls: ['./files-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: true,
  imports: [
    RowStatusMarkComponent,
    CommonModule,
    ListLoadErrorComponent,
    NoContentStubComponent,
    SelectableListDirective,
    LoadingIndicatorComponent,
    ListItemSeparatorComponent,
    ElementsModule,
    ClickDirective,
    SelectableItemDirective,
    ListSelectionBarComponent,
    Pagination2Component,
  ],
})
export class FilesListComponent extends BaseStitchListComponent<File> implements OnInit, OnDestroy, OnChanges {
  /** Show per-row folder-location badges (search results, where results span folders). */
  public readonly showLocationBadge = input<boolean>(false);
  public readonly requestContext = input<HttpContext>();
  public readonly tenantItems = input<ReadonlyArray<{ readonly accountId?: string }>>();

  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Container treatment forwarded to every card; the knowledge hub asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The hub draws its related items as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  public items: StitchListItem<File>[] = [];

  @Input()
  public override filters: Partial<FilesFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickFile = new EventEmitter<File>();
  @Output()
  public readonly openFile = new EventEmitter<File>();
  @Output()
  public readonly doubleClickFile = new EventEmitter<File>();

  @ViewChildren(FileComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  private readonly filesService = inject(FilesService);
  private readonly filesStateService = inject(FilesAppStateService);
  private readonly linkedInfoService = inject(LinkedInfoService);
  private readonly filesTenantScopeService = inject(FilesTenantScopeService, { optional: true });
  private readonly uploadSelection = inject(FilesUploadSelectionService);
  private readonly injector = inject(Injector);
  private readonly rowsSelection = viewChild(SelectableListDirective);
  private detachUploadSelection?: () => void;

  protected override stitchSignalSpaceIds(): ReadonlyArray<string> {
    const spaceIds = new Set<string>([this.spaceContextStore.contextId()]);
    const effectiveTenantId = this.filesTenantScopeService?.effectiveTenantId();
    if (effectiveTenantId) {
      spaceIds.add(effectiveTenantId);
    }
    for (const { accountId } of this.tenantItems() ?? []) {
      if (accountId) {
        spaceIds.add(accountId);
      }
    }
    return [...spaceIds];
  }

  protected override getStitchType(): StitchType {
    return StitchType.FILE;
  }

  public override ngOnInit() {
    this.detachUploadSelection = this.uploadSelection.attach();
    merge(this.filesStateService.getRefreshAll(), this.linkedInfoService.getRefresh(), this.filesService.getRefresh())
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });

    super.ngOnInit();
  }

  public override ngOnDestroy() {
    this.detachUploadSelection?.();
    super.ngOnDestroy();
  }

  protected override afterItemsLoaded(): void {
    const ids = this.uploadSelection.take(this.items.map((item) => item.data.id));
    if (ids.length === 0) {
      return;
    }
    const byId = new Map(this.items.map((item) => [item.data.id, item.data]));
    afterNextRender(
      () =>
        this.rowsSelection()?.selectableService.setSelectedItems(
          ids.map((id) => ({ identifier: id, data: byId.get(id) })),
        ),
      { injector: this.injector },
    );
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('filters' in changes) {
      this.resetItems();
    }
  }

  protected override getItems(offset: number, limit: number): Observable<{ items: File[]; count: number }> {
    return this.filesService.search(
      {
        ...this.filters,
        archived: this.filters?.archived ?? false,
        deleted: this.filters?.deleted ?? false,
        limit,
        offset,
      },
      {
        withTags: this.withTags,
        withKnots: this.withKnots,
        withConnections: false,
        knotsSource: this.knotsSource,
        tenantItems: this.tenantItems(),
        requestContext: this.requestContext(),
      },
    );
  }

  public handleDoubleClickItem(file: File) {
    if (hasFileLink(file)) {
      this.filesService.openLink(file);
      return;
    }
    this.doubleClickFile.emit(file);
  }

  public handleOpenSelected(event: SelectableOpenEvent<File>): void {
    // A refresh can advance the list selection after a restore. Keep the open Trash
    // form on its original file; opening a different file requires a user gesture.
    if (this.filters.deleted && event.source === 'removal') {
      return;
    }
    this.clickFile.emit(event.item.data);
  }

  protected override getSectionValue(item: File): unknown | undefined {
    if (this.filters.sortBy === 'attachmentSize') {
      return undefined;
    }
    return super.getSectionValue(item);
  }
}
